import { rawString } from '../components/Language/Language';
import type {
  Comment,
  Photo,
  ReasonItemData,
} from '../components/tasks/ReasonsTask/ReasonsTaskSchema';
import type { ReasonFieldVariables } from '../graphql/mutation/submit';
import {
  type Order,
  type OrderItem,
  type Reason,
  type Reasons,
} from '../graphql/query/getOrder';
import { queryClient } from '../queryClient';
import { type WorkflowData } from '../workflow';
import { QUERIES } from './constants';
import type { GetRetailerSettingsResult } from '../graphql/query/getRetailerSettings';
import { parseItemKey } from './itemKey';

export const getItemsFromOrder = (
  selectedItems: WorkflowData['items'],
  order: Order,
) => {
  const selectedItemIds = Object.keys(selectedItems || {});
  if (selectedItemIds.length === 0 || !order?.items) return [];

  return selectedItemIds
    .map((itemId) => {
      // First, try to find the item directly in order.items
      const directItem = order.items.find((i) => i.id === itemId);
      if (directItem) {
        return directItem;
      }

      // Handle virtual items using sourceItemId
      const itemData = selectedItems?.[itemId];
      const sourceItemId = itemData?.itemSelection?.sourceItemId;

      if (sourceItemId) {
        // Find the source item that this virtual item represents
        const sourceItem = order.items.find((i) => i.id === sourceItemId);
        if (sourceItem) {
          // Return the source item with the virtual item's ID for tracking
          return {
            ...sourceItem,
            id: itemId, // Use the virtual item's ID
          };
        }
      }

      // Handle compound keys using itemKey utility
      if (itemData?.itemSelection?.trackingNumber) {
        const { id: baseItemId, trackingNumber } = parseItemKey(itemId);

        // Only proceed if we actually have a tracking number in the parsed key
        if (trackingNumber) {
          const baseItem = order.items.find((i) => i.id === baseItemId);

          if (baseItem) {
            // Return the base item with the compound key ID for tracking
            return {
              ...baseItem,
              id: itemId, // Use the compound key ID
            };
          }
        }
      }

      return undefined;
    })
    .filter((item) => item !== undefined);
};

export const getItemDescription = ({
  item,
  quantity,
}: {
  item: Pick<OrderItem, 'variant'>;
  quantity?: number;
}) => {
  const fData = queryClient.getQueryData<GetRetailerSettingsResult>([
    QUERIES.GET_RETAILER_SETTINGS,
  ]);
  const itemAttributes = fData?.retailerSettings.features?.itemAttributes;
  if (!itemAttributes) return undefined;

  const { displayValueOnly, displayKeyValue } = itemAttributes;
  const variantInfo = (item.variant && item.variant.info) ?? [];

  const fragments: string[] = [];

  if (displayValueOnly?.length) {
    const valuesOnly = displayValueOnly.reduce<string[]>((acc, attr) => {
      const attrOnItem = variantInfo.find(({ name }) => name === attr);
      const attrValue = attrOnItem?.value;
      if (attrValue) {
        acc.push(attrValue);
      }
      return acc;
    }, []);

    if (valuesOnly.length) {
      fragments.push(valuesOnly.join(', '));
    }
  }

  if (displayKeyValue?.length) {
    const keyValues = displayKeyValue.reduce<string[]>((acc, attr) => {
      const attrOnItem = variantInfo.find(({ name }) => name === attr);
      const attrValue = attrOnItem?.value;
      if (attrValue) {
        const attrKeyLabel =
          rawString({
            langKey: `Global.item.attribute.${attr}`,
          }) ?? attr;
        acc.push(`${attrKeyLabel}: ${attrValue}`);
      }
      return acc;
    }, []);

    if (keyValues.length) {
      fragments.push(keyValues.join(', '));
    }
  }

  if (quantity && quantity > 1) {
    const quantityLabel = rawString({
      langKey: 'Global.item.attribute.quantity',
    });
    fragments.push(`${quantityLabel}: ${quantity}`);
  }

  return fragments.join(', ');
};

export const hasEligibleReason = (
  reasons: (Reason | null | undefined)[],
): boolean => {
  if (!reasons?.length) return false;

  const visited = new Set<string>();
  const stack: Reason[] = reasons.filter((r): r is Reason => r != null);

  while (stack.length) {
    const current = stack.pop()!;
    if (visited.has(current.code)) continue;
    visited.add(current.code);
    if (current.resolutions?.length) return true;
  }

  return false;
};

export const filterItemsByEligibility = (
  items: OrderItem[],
  isEligible = true,
) =>
  items.filter(({ reasons }) =>
    reasons ? isEligible === hasEligibleReason(reasons) : false,
  );

export type ExtractedReason = {
  reasons: Reasons;
  nestedReasons: Reasons;
  selectedRootReason: Reasons[number] | undefined;
  selectedNestedReason: Reasons[number] | undefined;
  selectedReason: Reasons[number] | undefined;
  isNestedReasonRequired: boolean;
  photoConfig?: {
    id?: string;
    enabled: boolean;
    required: boolean;
    max?: number;
  };
  commentConfig?: {
    metadataId?: string;
    enabled: boolean;
    required: boolean;
  };
  attestationConfig?: {
    metadataId?: string;
    enabled: boolean;
    required: boolean;
  };
  comments?: Comment;
  photos?: Photo[];
  attestation?: { checked?: boolean; metadataId?: string };
};

export const extractReasons = ({
  data,
  order,
}: {
  data: WorkflowData | null;
  order?: Order | null;
}) => {
  if (!order || !data) {
    return {};
  }
  const cartScopedReason = data.reason;
  const orderItems = getItemsFromOrder(data.items, order);

  if (!orderItems || orderItems.length === 0) {
    throw new Error('Cannot resolve without item data');
  }

  return orderItems.reduce(
    (acc: Record<OrderItem['id'], ExtractedReason>, orderItem) => {
      const { reason: { reasonId, nestedReasonId, comments, photos } = {} } =
        cartScopedReason ? data : (data.items?.[orderItem.id] ?? {});

      const { reasons } = orderItem;

      if (!reasons) {
        return acc;
      }

      const rootReasons = reasons.filter((r) => r?.parentId == null);
      const selectedRootReason = reasons.find(
        (r) => r?.code === reasonId && r?.parentId == null,
      );
      const nestedReasons = selectedRootReason
        ? reasons.filter((r) => r?.parentId === selectedRootReason.code)
        : [];
      const selectedNestedReason = nestedReasons.find(
        (r) => r?.code === nestedReasonId,
      );
      const selectedReason = selectedNestedReason || selectedRootReason;
      const commentField = selectedReason?.fields?.find(
        (f) => f?.type === 'COMMENT',
      );
      const photoField = selectedReason?.fields?.find(
        (f) => f?.type === 'PHOTO',
      );
      const attestationField = selectedReason?.fields?.find(
        (f) => f?.type === 'ATTESTATION',
      );
      const isNestedReasonRequired = Boolean(
        nestedReasons && nestedReasons.length > 0,
      );
      const commentConfig = {
        metadataId: commentField?.id ?? undefined,
        enabled: Boolean(commentField),
        required: Boolean(commentField && commentField.required),
      };
      const photoConfig = {
        id: photoField?.id ?? undefined,
        enabled: Boolean(photoField),
        required: Boolean(photoField && photoField.required),
        max: photoField?.max ?? undefined,
      };
      const attestationConfig = {
        metadataId: attestationField?.id ?? undefined,
        enabled: Boolean(attestationField),
        required: Boolean(attestationField && attestationField.required),
      };

      acc[orderItem.id] = {
        reasons: rootReasons,
        nestedReasons,
        selectedRootReason,
        selectedNestedReason,
        selectedReason,
        isNestedReasonRequired,
        commentConfig,
        photoConfig,
        attestationConfig,
        comments,
        photos,
        attestation:
          (cartScopedReason ? data : data.items?.[orderItem.id])?.reason
            ?.attestation ?? undefined,
      };

      return acc;
    },
    {},
  );
};

export const buildReasonFields = (
  reason: Pick<ReasonItemData, 'comments' | 'photos' | 'attestation'>,
): ReasonFieldVariables => {
  const fields: ReasonFieldVariables = [];

  if (reason.comments?.value) {
    fields.push({
      type: 'COMMENT',
      value: reason.comments.value,
      metadataId: reason.comments.metadataId,
    });
  }

  if (Array.isArray(reason.photos) && reason.photos.length > 0) {
    reason.photos.forEach((photo) => {
      fields.push({
        type: 'PHOTO',
        value: photo.id,
        metadataId: photo.metadataId,
      });
    });
  }

  if (reason.attestation?.checked === true) {
    fields.push({
      type: 'ATTESTATION',
      value: 'true',
      metadataId: reason.attestation?.metadataId,
    });
  }

  return fields;
};

export type ShipmentGroup = {
  shipmentIndex: number;
  trackingNumber: string;
  carrier: string;
  carrierService: string;
  shipDate: string;
  shipMethod: string;
  items: OrderItem[];
};

/**
 * Categorized buckets for claim item display
 */
export type ItemBuckets = {
  /** Items that are eligible for claiming (have reasons with resolutions and remaining > 0) */
  availableForClaim: OrderItem[];
  /** Items that have existing claims (claim history) */
  claimHistory: OrderItem[];
  /** Items that are ineligible (no reasons/resolutions, no claims) */
  ineligible: OrderItem[];
  /** Items not yet delivered (placeholder - needs delivery status data) */
  notYetDelivered: OrderItem[];
};

/**
 * Categorizes order items into display buckets for the item selection screen.
 *
 * Bucketing logic:
 * - Available for claim: has eligible reasons AND remaining quantity > 0
 * - Claim history: has any claims (item.claims.length > 0)
 * - Ineligible: no eligible reasons AND no claims
 * - Not yet delivered: placeholder for future (when delivery status is available)
 *
 * Note: An item can appear in both "claim history" (for claimed quantities)
 * and "available for claim" (for remaining quantities) if it has both.
 * The expandItemsWithClaims utility handles this split at render time.
 */
export const categorizeItemsIntoBuckets = (items: OrderItem[]): ItemBuckets => {
  const buckets: ItemBuckets = {
    availableForClaim: [],
    claimHistory: [],
    ineligible: [],
    notYetDelivered: [],
  };

  for (const item of items) {
    const hasClaims = (item.claims?.length ?? 0) > 0;
    const hasEligibleReasons = hasEligibleReason(item.reasons ?? []);
    const hasRemaining = (item.quantity?.remaining ?? 0) > 0;

    // Items with claims go to claim history
    // (they may also appear in available if they have remaining quantity)
    if (hasClaims) {
      buckets.claimHistory.push(item);
    }

    // Items with eligible reasons and remaining quantity go to available
    if (hasEligibleReasons && hasRemaining) {
      buckets.availableForClaim.push(item);
    }

    // Items without eligible reasons and without claims go to ineligible
    // (they still have remaining quantity that can't be claimed)
    if (!hasEligibleReasons && !hasClaims && hasRemaining) {
      buckets.ineligible.push(item);
    }

    // Not yet delivered: placeholder for future implementation
    // Will need delivery status data from shipments to populate this bucket
    // For now, this remains empty
  }

  return buckets;
};

/**
 * Groups order items by their shipments for display purposes
 */
export const groupItemsByShipment = (order: Order): ShipmentGroup[] => {
  if (!order.shipments || order.shipments.length === 0) {
    // If no shipments, return all items in a single group
    return [
      {
        shipmentIndex: 0,
        trackingNumber: '',
        carrier: '',
        carrierService: '',
        shipDate: '',
        shipMethod: '',
        items: order.items || [],
      },
    ];
  }

  // Create a separate group for each shipment to preserve individual shipments
  // even when items appear in multiple shipments
  return order.shipments.map((shipment, index) => {
    const shipmentItemIds =
      shipment.itemsInfo?.map((itemInfo) => itemInfo.itemId) || [];
    const items =
      order.items?.filter((item) => shipmentItemIds.includes(item.id)) || [];

    return {
      shipmentIndex: index,
      trackingNumber: shipment.trackingNumber || '',
      carrier: shipment.carrier || '',
      carrierService: shipment.carrierService || '',
      shipDate: shipment.shipDate || '',
      shipMethod: shipment.shipMethod || '',
      items,
    };
  });
};
